/**
 * @file 閲覧位置 (学習者 × 資料の、最後に開いたページ) の読み書き。
 *
 * @what: useReadingPositions は、まなぶ人の閲覧位置を資料 id ごとの Map で返す (本だなが
 *   「つづきから」と「最近開いた本」に使う)。useReadingPositionWriter は、リーダーが
 *   開いているページを数秒の間引きでサーバに送る (閉じるときは待たずに送る)。
 * @why: 送信の間引きは、ページ送りのたびに書くとリーダーの操作の数だけ書き込みが走る
 *   ためである。閉じるとき・タブを隠すとき・ページを離れるときに待たずに送るのは、間引きの
 *   待ちの間に離れると最後のページが別の端末に届かないためである (代表の判断、2026-09-14)。端末の localStorage への記録 (model/resumeReading.ts) は別に残す
 *   (送れなかったときの手がかり)。
 * @failure: 一覧の取得失敗は空の Map (本だなは出る。つづきの案内は端末の手がかりだけになる)。
 *   送信の失敗は console に残すだけで、読む操作は止めない。
 */

import { useEffect, useRef, useState } from "react";
import { fetchReadingPositions, putReadingPosition } from "../api/readingPositions";
import {
  beginDeviceSyncSend,
  deviceSyncId,
  endDeviceSyncSend,
  noteDeviceSyncSeen,
  takeDeviceSyncFollowed,
  useDeviceSyncEnabled,
} from "../lib/deviceSync";
import { newerOpenedPage, readLastOpenedPage } from "../model/resumeReading";

/** 資料ごとの閲覧位置 (ページと、開いた時刻の epoch ms) と、読んだページと秒数の累計である。 */
export interface ReadingPosition {
  page: number;
  at: number;
  /** これまでに読んだページ (連番、昇順) で、無ければ空になる。 */
  pagesRead: readonly number[];
  /** 実際に操作していた秒数の累計で、無ければ0になる。 */
  activeSeconds: number;
}

const EMPTY: ReadonlyMap<string, ReadingPosition> = new Map();

export function useReadingPositions(
  familyId: string,
  personId: string | null,
): ReadonlyMap<string, ReadingPosition> {
  const [map, setMap] = useState<ReadonlyMap<string, ReadingPosition>>(EMPTY);

  useEffect(() => {
    setMap(EMPTY);
    if (!personId) return;
    const controller = new AbortController();
    fetchReadingPositions(familyId, personId, controller.signal)
      .then((positions) => {
        if (controller.signal.aborted) return;
        const next = new Map<string, ReadingPosition>();
        for (const p of positions) {
          const at = Date.parse(p.opened_at);
          if (Number.isFinite(at))
            next.set(p.source_id, {
              page: p.page,
              at,
              pagesRead: p.pages_read ?? [],
              activeSeconds: p.active_seconds ?? 0,
            });
        }
        setMap(next);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        console.error("[study] failed to load reading positions", err);
      });
    return () => controller.abort();
  }, [familyId, personId]);

  return map;
}

/**
 * 本を開いたときに、前回開いていたページを1冊ぶん引く。
 *
 * @what: まなぶ人の閲覧位置 (サーバ) と端末に覚えたページの新しい方を返す (model/resumeReading.ts)。
 *   まなぶ人の候補を読み終えるまでは loading にする。まなぶ人がいなければ端末に覚えたページだけで決める。
 * @why: どの入口から開いても、どの端末でも、前回開いていたページから始める (代表の判断、2026-09-14)。
 *   読み終える前に紙面を出すと、先頭のページを閲覧位置として送り、前回のページを上書きしてしまう。
 * @failure: 閲覧位置の取得に失敗したら、端末に覚えたページだけで決める (console に残す)。
 */
export type ResumePageState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "ready"; page: number | null };

export function useResumePage(
  familyId: string,
  sourceId: string,
  learner: { ready: boolean; personId: string | null },
  pageCount: number | undefined,
  enabled: boolean,
): ResumePageState {
  const [state, setState] = useState<ResumePageState>({ status: "idle" });
  const { ready, personId } = learner;

  useEffect(() => {
    if (!enabled) {
      setState({ status: "idle" });
      return;
    }
    if (!ready) {
      setState({ status: "loading" });
      return;
    }
    const local = readLastOpenedPage(familyId, sourceId);
    if (!personId) {
      setState({ status: "ready", page: newerOpenedPage(null, local, pageCount) });
      return;
    }
    const controller = new AbortController();
    setState({ status: "loading" });
    fetchReadingPositions(familyId, personId, controller.signal)
      .then((positions) => {
        if (controller.signal.aborted) return;
        const found = positions.find((p) => p.source_id === sourceId);
        const at = found ? Date.parse(found.opened_at) : NaN;
        const server = found && Number.isFinite(at) ? { page: found.page, at } : null;
        setState({ status: "ready", page: newerOpenedPage(server, local, pageCount) });
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        console.error("[study] failed to load the reading position to resume", err);
        setState({ status: "ready", page: newerOpenedPage(null, local, pageCount) });
      });
    return () => controller.abort();
  }, [familyId, sourceId, ready, personId, pageCount, enabled]);

  return state;
}

/** 送信までの待ち (ms)。ページ送りのたびに延びる。 */
export const READING_POSITION_DELAY_MS = 1500;

/**
 * ほかの端末と同じページを開く連動を入れているときの、送信までの待ち (ms) である。
 * 続けてめくったときに1枚ごとに送らない程度に短くする (ほかの端末がついてくるまでの遅れになる)。
 */
export const READING_POSITION_SYNC_DELAY_MS = 300;

/**
 * 開いているページをまなぶ人の閲覧位置としてサーバに送る (間引きあり)。
 *
 * @what: page が変わるたびに待ちを取り直し、止まったら送る。同じページを送り直さない。
 *   まなぶ人が未定なら送らない。unmount (リーダーを閉じる) では待たずに送る。
 *   ほかの端末と同じページを開く連動を入れているときは、待ちを短くし、端末の印を付けて
 *   ほかの端末へ知らせる。ほかの端末についていったページは送り返さない (送り返すと、
 *   遅れて届いた古いページへ相手を連れ戻すことがある)。
 */
export function useReadingPositionWriter(
  familyId: string,
  personId: string | null,
  sourceId: string,
  page: number | null,
): void {
  // まだ送っていないページ (null = 送るものが無い)。
  const pending = useRef<number | null>(null);
  const lastSent = useRef<number | null>(null);
  const syncOn = useDeviceSyncEnabled();
  // unmount のときに参照する (effect のクロージャは古い値を掴むため)。
  const ids = useRef({ familyId, personId, sourceId, syncOn });
  ids.current = { familyId, personId, sourceId, syncOn };

  useEffect(() => {
    if (page === null || !personId) return;
    const followed = syncOn && takeDeviceSyncFollowed(sourceId, page);
    if (followed || page === lastSent.current) {
      lastSent.current = page;
      pending.current = null;
      return;
    }
    pending.current = page;
    // 連動しているときは、待ちの間も「送っている途中」に数える (ついていく側がこの送信を待つ)
    if (syncOn) beginDeviceSyncSend();
    let fired = false;
    const timer = window.setTimeout(() => {
      fired = true;
      pending.current = null;
      lastSent.current = page;
      const sync = syncOn ? { sync: { deviceId: deviceSyncId() } } : undefined;
      putReadingPosition(familyId, personId, sourceId, page, sync)
        .then((saved) => {
          if (syncOn) noteDeviceSyncSeen(Date.parse(saved.opened_at));
        })
        .catch((err: unknown) => {
          console.warn("[study] failed to save the reading position", err);
        })
        .finally(() => {
          if (syncOn) endDeviceSyncSend();
        });
    }, syncOn ? READING_POSITION_SYNC_DELAY_MS : READING_POSITION_DELAY_MS);
    return () => {
      window.clearTimeout(timer);
      if (syncOn && !fired) endDeviceSyncSend();
    };
  }, [familyId, personId, sourceId, page, syncOn]);

  useEffect(() => {
    // 待っているページを、待たずに送る。ページを離れるときも届くよう keepalive を付ける
    const flush = () => {
      const { familyId: f, personId: p, sourceId: s, syncOn: on } = ids.current;
      const last = pending.current;
      if (last === null || !p) return;
      pending.current = null;
      lastSent.current = last;
      const opts = on ? { keepalive: true, sync: { deviceId: deviceSyncId() } } : { keepalive: true };
      putReadingPosition(f, p, s, last, opts).catch((err: unknown) => {
        console.warn("[study] failed to save the reading position", err);
      });
    };
    const onHide = () => {
      if (document.visibilityState === "hidden") flush();
    };
    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("pagehide", flush);
    return () => {
      document.removeEventListener("visibilitychange", onHide);
      window.removeEventListener("pagehide", flush);
      flush();
    };
  }, []);
}
